import { RawDraftContentState, RawDraftEntity } from 'draft-js';
import type { StoreState } from '@common/types/store';

interface User {
  id: string;
  full_name: string;
  avatar?: string;
  [key: string]: any;
}

export function createMentionEntities(
  rawContent: RawDraftContentState,
  state: StoreState
): RawDraftContentState {
  const mentionRegex = /@\((\d+)\)/g;
  const entityMap: Record<string, RawDraftEntity> = { ...rawContent.entityMap };
  let entityCounter = Object.keys(entityMap).length;

  const blocks = rawContent.blocks.map((block) => {
    const { text } = block;
    const existingRanges = [...(block.entityRanges || [])];
    let modifiedText = text;

    const mentions: Array<{ match: RegExpExecArray; userId: string; user: User }> = [];
    let match;

    mentionRegex.lastIndex = 0;

    // eslint-disable-next-line no-cond-assign
    while ((match = mentionRegex.exec(text)) !== null) {
      const userId = match[1];
      const user = state.users.items[userId] as User | undefined;

      if (user && user.full_name) {
        mentions.push({ match, userId, user });
      }
    }

    const mentionRanges: Array<{ offset: number; length: number; key: number }> = [];

    mentions.forEach(({ match: matchValue, user }, index) => {
      const originalStart = matchValue.index;
      const matchLength = matchValue[0].length;
      let currentOffset = originalStart;
      for (let i = 0; i < index; i++) {
        const prevMention = mentions[i];
        const prevEnd = prevMention.match.index + prevMention.match[0].length;
        if (prevEnd >= text.length || text[prevEnd] !== ' ') {
          currentOffset++;
        }
      }

      const matchStart = currentOffset;

      const entityKey = String(entityCounter++);
      entityMap[entityKey] = {
        type: 'mention',
        mutability: 'IMMUTABLE',
        data: {
          mention: {
            id: user.id,
            full_name: user.full_name,
            avatar: user.avatar || undefined,
          },
        },
      };

      mentionRanges.push({
        offset: matchStart,
        length: matchLength,
        key: Number(entityKey),
      });
    });

    let textOffset = 0;
    mentions.forEach(({ match: matchData }) => {
      const originalEnd = matchData.index + matchData[0].length;
      const currentEnd = originalEnd + textOffset;

      if (currentEnd >= modifiedText.length || modifiedText[currentEnd] !== ' ') {
        modifiedText = `${modifiedText.slice(0, currentEnd)} ${modifiedText.slice(currentEnd)}`;
        textOffset++;
      }
    });

    const allRanges = [...existingRanges, ...mentionRanges];

    const sortedRanges = allRanges
      .slice()
      .sort((a, b) => a.offset - b.offset)
      .filter((range, idx, arr) => {
        if (idx === 0) return true;
        const prev = arr[idx - 1];
        return !(prev.offset === range.offset && prev.length === range.length && prev.key === range.key);
      });

    return {
      ...block,
      text: modifiedText,
      entityRanges: sortedRanges,
    };
  });

  return {
    ...rawContent,
    blocks,
    entityMap,
  };
}
